import { studioSelectionSummary } from 'src/components/star-grid/utils/studioMultiselect';
import React from 'react';
import Box from '@amzn/awsui-components-core/box';
import Button from '@amzn/awsui-components-core/button';
import SpaceBetween from '@amzn/awsui-components-core/space-between';
import TokenGroup from '@amzn/awsui-components-core/token-group';
import type { ReportParameter } from 'src/components/star-grid/reportDefinition.types';
import { useStarGridText } from 'src/components/star-grid/hooks/useStarGridText';
import { useReportParameter } from 'src/components/star-grid/hooks/useReportParameter';
import { useReportHierarchySelection } from 'src/components/star-grid/hooks/useReportHierarchySelection';
import MemberSubsetModal from 'src/components/star-grid/components/subset-editor/MemberSubsetModal';
import { HierarchyFilterDropdown } from 'src/components/star-grid/components/HierarchyFilterDropdown';
import styles from 'src/components/star-grid/components/styles/SavedReportFilters.module.scss';

interface Props {
  readonly parameter: ReportParameter;
  readonly values: readonly string[];
  readonly state: ReturnType<typeof useReportParameter>;
  readonly disabled?: boolean;
  readonly compact?: boolean;
  readonly onChange: (key: string, values: readonly string[]) => void;
}
/** The subset editor path: its own component so its hooks are never conditional on the branch below. */
const ReportHierarchySubsetPicker: React.FC<Props> = ({ parameter, values, state, disabled, compact, onChange }) => {
  const text = useStarGridText();
  const selection = useReportHierarchySelection(parameter, values, state.tree, onChange);
  // The panel is viewport-fixed (the filter bar clips absolutely positioned children), so it needs
  // the trigger's on-screen position. Measured at open time rather than tracked continuously.
  const anchor = React.useRef<HTMLDivElement>(null);
  const [panelAt, setPanelAt] = React.useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const openPanel = React.useCallback(() => {
    const rect = anchor.current?.getBoundingClientRect();
    if (rect) setPanelAt({ top: rect.bottom + 4, left: rect.left });
    selection.open();
  }, [selection]);
  return (
    <SpaceBetween size="xs">
      <div ref={anchor} />
      <Button
        data-testid={`defined-parameter-${parameter.key}`}
        formAction="none"
        iconName={compact ? undefined : 'filter'}
        ariaHaspopup="dialog"
        ariaExpanded={selection.isOpen}
        nativeButtonAttributes={{ id: `defined-parameter-${parameter.key}`, title: state.selected.map((option) => option.label).join(', ') }}
        loading={state.loading}
        disabled={disabled || !state.tree}
        onClick={openPanel}
        ariaLabel={`${text('selectMembers')}: ${parameter.label}`}
      >
        {compact && state.selected.length ? studioSelectionSummary(state.selected, text) : text('selectMembers')}
      </Button>
      {!compact && (
        <>
          <Box variant="small" color="text-body-secondary">
            {text(parameter.multiple ? 'multipleMembersHelp' : 'singleMemberHelp')}
          </Box>
          <TokenGroup
            items={state.selected}
            limit={3}
            readOnly={disabled}
            onDismiss={({ detail }) =>
              onChange(
                parameter.key,
                // Remove the dismissed token by index, not by value: duplicate member ids must not all drop.
                values.filter((_, i) => i !== detail.itemIndex)
              )
            }
          />
        </>
      )}
      {selection.isOpen &&
        state.tree &&
        // In a report (compact), the member picker is a hierarchical dropdown anchored under the
        // field -- matching the Prophecy P&L deck -- not a full-screen modal. The subset editor's
        // ``embedded`` mode renders the same tree inline; a backdrop closes it on outside click.
        (compact ? (
          <div className={styles.hierarchyDropdown}>
            <button type="button" aria-label="Close member picker" className={styles.hierarchyBackdrop} onClick={selection.dismiss} />
            <div className={styles.hierarchyPanel} style={{ top: panelAt.top, left: panelAt.left }} role="dialog" aria-label={parameter.label}>
              <MemberSubsetModal
                embedded
                hierarchyId={state.tree.hierarchy_id}
                hierarchyTree={state.tree}
                dimensionLabel={parameter.label}
                visible
                multiple={parameter.multiple}
                initialChecked={selection.initialChecked}
                onConfirm={selection.confirm}
                onDismiss={selection.dismiss}
                testId={`defined-members-${parameter.key}`}
              />
            </div>
          </div>
        ) : (
          <MemberSubsetModal
            hierarchyId={state.tree.hierarchy_id}
            hierarchyTree={state.tree}
            dimensionLabel={parameter.label}
            visible
            multiple={parameter.multiple}
            initialChecked={selection.initialChecked}
            onConfirm={selection.confirm}
            onDismiss={selection.dismiss}
            testId={`defined-members-${parameter.key}`}
          />
        ))}
    </SpaceBetween>
  );
};

/**
 * A hierarchy filter on a report screen.
 *
 * In a report (compact) it is ONE control: a pill showing the selection over a member tree, matching
 * the P&L deck. The heavier subset editor stays for the config screens, where choosing a subset is the
 * task rather than a filter on a report being read.
 */
export const ReportHierarchyParameter: React.FC<Props> = (props) => {
  const text = useStarGridText();
  const { parameter, values, state, disabled, compact, onChange } = props;
  if (!compact || !state.tree) return <ReportHierarchySubsetPicker {...props} />;
  return (
    <HierarchyFilterDropdown
      id={`defined-parameter-${parameter.key}`}
      label={parameter.label}
      members={state.tree.members}
      values={values}
      multiple={parameter.multiple}
      scopeRoot={parameter.scope_root}
      disabled={disabled}
      loading={state.loading}
      text={text}
      onChange={(next) => onChange(parameter.key, next)}
    />
  );
};
